import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../utils/api';
import ProtectedRoute from '../../components/ProtectedRoute';
import AdminLayout, { useTheme } from '../../components/AdminLayout';
import { useNotification } from '../../contexts/NotificationContext';

const STATUS_OPTIONS = ['present', 'absent', 'late', 'sick'];

const STATUS_STYLES = {
  present: { bg: '#dcfce7', color: '#15803d', border: '#bbf7d0' },
  absent: { bg: '#fee2e2', color: '#b91c1c', border: '#fecaca' },
  late: { bg: '#fef3c7', color: '#92400e', border: '#fde68a' },
  sick: { bg: '#dbeafe', color: '#1d4ed8', border: '#bfdbfe' },
};

function StatusPill({ status }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.present;
  return (
    <span style={{
      display: 'inline-block',
      padding: '0.2rem 0.65rem',
      borderRadius: '999px',
      fontSize: '0.72rem',
      fontWeight: 700,
      fontFamily: "'DM Sans', sans-serif",
      background: s.bg,
      color: s.color,
      border: `1px solid ${s.border}`,
      textTransform: 'capitalize',
    }}
    >
      {status}
    </span>
  );
}

function todayStr() {
  return new Date().toISOString().slice(0, 10);
}

function AttendanceContent() {
  const { t } = useTranslation();
  const { showAlert } = useNotification();
  const { T, S } = useTheme();

  const [shops, setShops] = useState([]);
  const [branches, setBranches] = useState([]);
  const [selectedShopId, setSelectedShopId] = useState('');
  const [selectedBranchId, setSelectedBranchId] = useState(null);
  const [managers, setManagers] = useState([]);
  const [artists, setArtists] = useState([]);
  const [workerAttendance, setWorkerAttendance] = useState([]);
  const [bmAttendance, setBmAttendance] = useState([]);
  const [byBranchData, setByBranchData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('workers');
  const [date, setDate] = useState(todayStr());
  const [dateFrom, setDateFrom] = useState(todayStr());
  const [dateTo, setDateTo] = useState(todayStr());
  const [workerForm, setWorkerForm] = useState({});
  const [bmForm, setBmForm] = useState({});

  useEffect(() => {
    api.getAdminShops().then(setShops).catch(() => setShops([]));
    api.getAdminAssignableBranchManagers().then(setManagers).catch(() => setManagers([]));
  }, []);

  useEffect(() => {
    if (!selectedShopId) {
      setBranches([]);
      setSelectedBranchId(null);
      return;
    }
    api.getAdminBranches(parseInt(selectedShopId, 10))
      .then(setBranches)
      .catch(() => setBranches([]));
  }, [selectedShopId]);

  const effectiveBranchId = selectedBranchId;

  useEffect(() => {
    if (!effectiveBranchId) {
      setArtists([]);
      return;
    }
    setLoading(true);
    api.getAdminArtists({ branchId: effectiveBranchId })
      .then((data) => setArtists(Array.isArray(data) ? data : []))
      .catch(() => setArtists([]))
      .finally(() => setLoading(false));
  }, [effectiveBranchId]);

  useEffect(() => {
    if (!effectiveBranchId) {
      setWorkerAttendance([]);
      setWorkerForm({});
      return;
    }
    setLoading(true);
    api.getAdminWorkerAttendance({ branchId: effectiveBranchId, dateFrom: date, dateTo: date })
      .then((data) => {
        setWorkerAttendance(Array.isArray(data) ? data : []);
        const form = {};
        (data || []).forEach((a) => {
          form[a.artist_id] = { status: a.status, hours_worked: a.hours_worked ?? '' };
        });
        setWorkerForm(form);
      })
      .catch(() => { setWorkerAttendance([]); setWorkerForm({}); })
      .finally(() => setLoading(false));
  }, [effectiveBranchId, date]);

  useEffect(() => {
    api.getAdminBranchManagerAttendance({ branchId: selectedBranchId || null, dateFrom: date, dateTo: date })
      .then((data) => {
        setBmAttendance(Array.isArray(data) ? data : []);
        const form = {};
        (data || []).forEach((a) => {
          form[a.user_id] = { status: a.status, hours_worked: a.hours_worked ?? '' };
        });
        setBmForm(form);
      })
      .catch(() => { setBmAttendance([]); setBmForm({}); });
  }, [selectedBranchId, date]);

  useEffect(() => {
    if (activeTab !== 'review') return;
    const shopId = selectedShopId ? parseInt(selectedShopId, 10) : null;
    api.getAdminAttendanceByBranch({ shopId, dateFrom, dateTo })
      .then(setByBranchData)
      .catch(() => setByBranchData([]));
  }, [activeTab, selectedShopId, dateFrom, dateTo]);

  const filteredArtists = artists.filter((a) => !effectiveBranchId || a.branch_id === effectiveBranchId);
  const branchManagersForBranch = managers.filter((m) => !selectedBranchId || m.branch_id === selectedBranchId);

  useEffect(() => {
    if (filteredArtists.length && Object.keys(workerForm).length === 0 && !loading) {
      const form = {};
      filteredArtists.forEach((a) => {
        const existing = workerAttendance.find((w) => w.artist_id === a.id);
        form[a.id] = existing
          ? { status: existing.status, hours_worked: existing.hours_worked ?? '' }
          : { status: 'present', hours_worked: '' };
      });
      setWorkerForm(form);
    }
  }, [filteredArtists, workerAttendance, loading]);

  const updateWorkerForm = (artistId, field, value) => {
    setWorkerForm((prev) => ({
      ...prev,
      [artistId]: {
        ...(prev[artistId] || { status: 'present', hours_worked: '' }),
        [field]: value,
      },
    }));
  };

  const updateBmForm = (userId, field, value) => {
    setBmForm((prev) => ({
      ...prev,
      [userId]: {
        ...(prev[userId] || { status: 'present', hours_worked: '' }),
        [field]: value,
      },
    }));
  };

  const saveWorkerAttendance = async () => {
    if (!effectiveBranchId) {
      showAlert(t('attendance.selectBranch') || 'Select a branch first');
      return;
    }
    setSaving(true);
    try {
      for (const a of filteredArtists) {
        const f = workerForm[a.id] || { status: 'present', hours_worked: '' };
        const payload = {
          artist_id: a.id,
          date,
          status: f.status,
          hours_worked: f.hours_worked ? parseFloat(f.hours_worked) : null,
        };
        const existing = workerAttendance.find((w) => w.artist_id === a.id);
        if (existing) {
          await api.updateAdminWorkerAttendance(existing.id, { status: payload.status, hours_worked: payload.hours_worked });
        } else {
          await api.createAdminWorkerAttendance(payload);
        }
      }
      showAlert(t('attendance.saved') || 'Attendance saved');
      const data = await api.getAdminWorkerAttendance({ branchId: effectiveBranchId, dateFrom: date, dateTo: date });
      setWorkerAttendance(Array.isArray(data) ? data : []);
    } catch (err) {
      showAlert(err.message || (t('attendance.saveFailed') || 'Failed to save'));
    } finally {
      setSaving(false);
    }
  };

  const saveBmAttendance = async () => {
    setSaving(true);
    try {
      for (const m of branchManagersForBranch) {
        const f = bmForm[m.id] || { status: 'present', hours_worked: '' };
        const payload = {
          user_id: m.id,
          date,
          status: f.status,
          hours_worked: f.hours_worked ? parseFloat(f.hours_worked) : null,
        };
        const existing = bmAttendance.find((b) => b.user_id === m.id);
        if (existing) {
          await api.updateAdminBranchManagerAttendance(existing.id, { status: payload.status, hours_worked: payload.hours_worked });
        } else {
          await api.createAdminBranchManagerAttendance(payload);
        }
      }
      showAlert(t('attendance.saved') || 'Attendance saved');
      const data = await api.getAdminBranchManagerAttendance({ branchId: selectedBranchId || null, dateFrom: date, dateTo: date });
      setBmAttendance(Array.isArray(data) ? data : []);
    } catch (err) {
      showAlert(err.message || (t('attendance.saveFailed') || 'Failed to save'));
    } finally {
      setSaving(false);
    }
  };

  const markAllPresent = () => {
    const form = {};
    filteredArtists.forEach((a) => {
      form[a.id] = { status: 'present', hours_worked: workerForm[a.id]?.hours_worked ?? '' };
    });
    setWorkerForm(form);
  };

  const tabStyle = (active) => ({
    background: 'none',
    border: 'none',
    color: active ? T.orange : T.muted,
    padding: '0.5rem 1rem',
    cursor: 'pointer',
    fontWeight: active ? 600 : 500,
    fontFamily: "'DM Sans', sans-serif",
    fontSize: '0.9rem',
    borderBottom: active ? `2px solid ${T.orange}` : '2px solid transparent',
    transition: 'color 0.15s',
  });

  return (
    <AdminLayout role="admin">
      <div style={{ marginBottom: '1.75rem' }}>
        <h1 style={S.pageTitle}>{t('attendance.title') || 'Attendance'}</h1>
        <div style={{ width: '40px', height: '3px', background: T.orange, borderRadius: '2px', marginTop: '0.3rem' }} />
      </div>

      <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '1.25rem' }}>
        <div>
          <label style={{ display: 'block', fontSize: '0.78rem', color: T.muted, marginBottom: '0.35rem' }}>{t('admin.selectSalon')}</label>
          <select
            value={selectedShopId}
            onChange={(e) => {
              setSelectedShopId(e.target.value);
              setSelectedBranchId(null);
            }}
            style={{ ...S.select, width: 'auto', marginBottom: 0, minWidth: 180 }}
          >
            <option value="">{t('admin.chooseSalon')}</option>
            {shops.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '0.78rem', color: T.muted, marginBottom: '0.35rem' }}>{t('attendance.branch')}</label>
          <select
            value={selectedBranchId ?? ''}
            onChange={(e) => setSelectedBranchId(e.target.value ? parseInt(e.target.value, 10) : null)}
            style={{ ...S.select, width: 'auto', marginBottom: 0, minWidth: 180 }}
            disabled={!selectedShopId}
          >
            <option value="">{t('attendance.allBranches')}</option>
            {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 0, marginBottom: '1.5rem', borderBottom: `1px solid ${T.border}` }}>
        <button type="button" style={tabStyle(activeTab === 'workers')} onClick={() => setActiveTab('workers')}>
          {t('attendance.workerAttendance') || 'Worker Attendance'}
        </button>
        <button type="button" style={tabStyle(activeTab === 'branch-managers')} onClick={() => setActiveTab('branch-managers')}>
          {t('attendance.branchManagerAttendance') || 'Branch Manager Attendance'}
        </button>
        <button type="button" style={tabStyle(activeTab === 'review')} onClick={() => setActiveTab('review')}>
          {t('attendance.reviewByBranch') || 'Review by Branch'}
        </button>
      </div>

      {activeTab === 'workers' && (
        <div style={S.card}>
          <div style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
            <label style={{ fontSize: '0.78rem', color: T.muted }}>{t('attendance.date')}</label>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} style={{ ...S.input, width: 'auto', marginBottom: 0 }} />
            <button
              type="button"
              style={{ ...S.btnOutline, marginBottom: 0 }}
              onClick={markAllPresent}
              disabled={filteredArtists.length === 0}
            >
              ✓
              {' '}
              {t('attendance.markAllPresent') || 'Mark All Present'}
            </button>
            <button
              type="button"
              style={{ ...S.btnOrange, marginBottom: 0 }}
              onClick={saveWorkerAttendance}
              disabled={saving || !effectiveBranchId || filteredArtists.length === 0}
            >
              {saving ? (t('common.saving') || 'Saving...') : (t('common.save') || 'Save')}
            </button>
          </div>
          {!effectiveBranchId ? (
            <p style={{ color: T.muted }}>{t('attendance.selectBranch') || 'Select a branch first'}</p>
          ) : loading ? (
            <p style={{ color: T.muted }}>{t('common.loading')}</p>
          ) : filteredArtists.length === 0 ? (
            <p style={{ color: T.muted }}>{t('attendance.noWorkers')}</p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={S.table}>
                <thead>
                  <tr>
                    <th style={S.th}>{t('attendance.worker')}</th>
                    <th style={S.th}>{t('attendance.status')}</th>
                    <th style={S.th}>{t('attendance.hoursWorked')}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredArtists.map((a) => (
                    <tr key={a.id}>
                      <td style={S.td}>{a.name}</td>
                      <td style={S.td}>
                        <select
                          value={(workerForm[a.id] || {}).status || 'present'}
                          onChange={(e) => updateWorkerForm(a.id, 'status', e.target.value)}
                          style={{ ...S.select, marginBottom: 0, minWidth: 100 }}
                        >
                          {STATUS_OPTIONS.map((s) => (
                            <option key={s} value={s}>{t(`attendance.status_${s}`) || s}</option>
                          ))}
                        </select>
                      </td>
                      <td style={S.td}>
                        <input
                          type="number"
                          step="0.5"
                          min="0"
                          placeholder="—"
                          value={(workerForm[a.id] || {}).hours_worked ?? ''}
                          onChange={(e) => updateWorkerForm(a.id, 'hours_worked', e.target.value)}
                          style={{ ...S.input, width: 80, marginBottom: 0 }}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTab === 'branch-managers' && (
        <div style={S.card}>
          <div style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <label style={{ fontSize: '0.78rem', color: T.muted }}>{t('attendance.date')}</label>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} style={{ ...S.input, width: 'auto', marginBottom: 0 }} />
            <button type="button" style={{ ...S.btnOrange, marginBottom: 0 }} onClick={saveBmAttendance} disabled={saving || branchManagersForBranch.length === 0}>
              {saving ? (t('common.saving') || 'Saving...') : (t('common.save') || 'Save')}
            </button>
          </div>
          {branchManagersForBranch.length === 0 ? (
            <p style={{ color: T.muted }}>{t('attendance.noBranchManagers')}</p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={S.table}>
                <thead>
                  <tr>
                    <th style={S.th}>{t('attendance.branchManager')}</th>
                    <th style={S.th}>{t('attendance.status')}</th>
                    <th style={S.th}>{t('attendance.hoursWorked')}</th>
                  </tr>
                </thead>
                <tbody>
                  {branchManagersForBranch.map((m) => (
                    <tr key={m.id}>
                      <td style={S.td}>
                        {m.name}
                        {' '}
                        {m.email && <span style={{ color: T.muted, fontSize: '0.8rem' }}>({m.email})</span>}
                      </td>
                      <td style={S.td}>
                        <select
                          value={(bmForm[m.id] || {}).status || 'present'}
                          onChange={(e) => updateBmForm(m.id, 'status', e.target.value)}
                          style={{ ...S.select, marginBottom: 0, minWidth: 100 }}
                        >
                          {STATUS_OPTIONS.map((s) => (
                            <option key={s} value={s}>{t(`attendance.status_${s}`) || s}</option>
                          ))}
                        </select>
                      </td>
                      <td style={S.td}>
                        <input
                          type="number"
                          step="0.5"
                          min="0"
                          placeholder="—"
                          value={(bmForm[m.id] || {}).hours_worked ?? ''}
                          onChange={(e) => updateBmForm(m.id, 'hours_worked', e.target.value)}
                          style={{ ...S.input, width: 80, marginBottom: 0 }}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTab === 'review' && (
        <div style={S.card}>
          <div style={{ marginBottom: '1rem', display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ fontSize: '0.78rem', color: T.muted }}>{t('attendance.dateFrom')}</span>
              <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} style={{ ...S.input, width: 'auto', marginBottom: 0 }} />
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ fontSize: '0.78rem', color: T.muted }}>{t('attendance.dateTo')}</span>
              <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} style={{ ...S.input, width: 'auto', marginBottom: 0 }} />
            </label>
          </div>
          {byBranchData.length === 0 ? (
            <p style={{ color: T.muted }}>{t('attendance.noData')}</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
              {byBranchData.map((br) => (
                <div key={br.branch_id} style={{ border: `1px solid ${T.border}`, borderRadius: 8, overflow: 'hidden' }}>
                  <div style={{ padding: '0.75rem 1rem', background: T.bg, borderBottom: `1px solid ${T.border}`, fontWeight: 600, color: T.white }}>
                    {br.branch_name}
                    <span style={{ marginLeft: '0.75rem', fontSize: '0.85rem', fontWeight: 500, color: T.muted }}>
                      {t('attendance.workersPresent')}
                      :
                      {' '}
                      {br.summary?.workers_present ?? 0}
                      {' '}
                      /
                      {' '}
                      {t('attendance.branchManagersPresent')}
                      :
                      {' '}
                      {br.summary?.branch_managers_present ?? 0}
                    </span>
                  </div>
                  <div style={{ padding: '1rem' }}>
                    {br.workers?.length > 0 && (
                      <div style={{ marginBottom: '1rem' }}>
                        <div style={{ fontSize: '0.78rem', color: T.muted, marginBottom: '0.5rem', textTransform: 'uppercase' }}>{t('attendance.workers')}</div>
                        <table style={{ ...S.table, marginBottom: 0 }}>
                          <thead>
                            <tr>
                              <th style={S.th}>{t('attendance.name')}</th>
                              <th style={S.th}>{t('attendance.date')}</th>
                              <th style={S.th}>{t('attendance.status')}</th>
                              <th style={S.th}>{t('attendance.hoursWorked')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {br.workers.map((w, i) => (
                              <tr key={i}>
                                <td style={S.td}>{w.name}</td>
                                <td style={S.td}>{w.date}</td>
                                <td style={S.td}><StatusPill status={w.status} /></td>
                                <td style={S.td}>{w.hours_worked ?? '—'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                    {br.branch_managers?.length > 0 && (
                      <div>
                        <div style={{ fontSize: '0.78rem', color: T.muted, marginBottom: '0.5rem', textTransform: 'uppercase' }}>{t('attendance.branchManagers')}</div>
                        <table style={{ ...S.table, marginBottom: 0 }}>
                          <thead>
                            <tr>
                              <th style={S.th}>{t('attendance.name')}</th>
                              <th style={S.th}>{t('attendance.date')}</th>
                              <th style={S.th}>{t('attendance.status')}</th>
                              <th style={S.th}>{t('attendance.hoursWorked')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {br.branch_managers.map((bm, i) => (
                              <tr key={i}>
                                <td style={S.td}>{bm.name}</td>
                                <td style={S.td}>{bm.date}</td>
                                <td style={S.td}><StatusPill status={bm.status} /></td>
                                <td style={S.td}>{bm.hours_worked ?? '—'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                    {(!br.workers?.length && !br.branch_managers?.length) && (
                      <p style={{ color: T.muted, fontSize: '0.9rem' }}>{t('attendance.noRecords')}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </AdminLayout>
  );
}

export default function AdminAttendance() {
  return (
    <ProtectedRoute requireSuperAdmin>
      <AttendanceContent />
    </ProtectedRoute>
  );
}
